import { MinusOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, Modal, Table, Tag, TimePicker } from 'antd';
import dayjs from 'libs/dayjs';
import {
  SOLICITUD_TIPOS,
  SOLICITUD_TIPOS_ARRAY,
  getColorForStatus,
} from 'assets/data/SolicitudesEstados';
import SpecialistSelect from 'components/selects/SpecialistSelect';
import { useCalendarActions } from 'providers/CalendarSolc/CalendarActionsProvider';
import { useSolicitudesMantencion } from 'providers/SolicitudesMantencionProvider';
import { useSolicitudes } from 'providers/SolicitudesProvider';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import paths from 'assets/routes/paths';

const SolicitudesForDayTable = () => {
  const {
    hasCalled,
    call,
    isLoading,
    deleteSolicitudToDay,
    selectedData,
    asignSpecialist,
    setHoraInicio,
  } = useSolicitudes();
  const { openDowloadMantencion } = useSolicitudesMantencion();
  const [open, setOpen] = useState(false);
  const { selectedDay } = useCalendarActions();

  useEffect(() => {
    if (!hasCalled) {
      call();
    }
  }, [hasCalled, call]);

  const closeModal = () => {
    setOpen(false);
  };

  return (
    <div className="flex-1">
      <Modal open={open} onCancel={closeModal} footer={false}></Modal>

      {/* Header con información del día */}
      <div className="bg-white rounded-lg shadow-sm border mb-4">
        <div className="px-4 py-3 border-b bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                {selectedDay
                  ? `Solicitudes del ${selectedDay?.format('dddd, D [de] MMMM [de] YYYY')}`
                  : 'Seleccione un día para ver las solicitudes'}
              </h3>
              {selectedDay && (
                <p className="text-xs text-gray-500 mt-1">{selectedDay?.format('DD/MM/YYYY')}</p>
              )}
            </div>
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
              {selectedData?.length || 0} solicitudes
            </span>
          </div>
        </div>
      </div>

      {/* Tabla compacta */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <Table
          loading={isLoading}
          size="small"
          className="compact-table"
          scroll={{ x: 600 }}
          dataSource={selectedData}
          columns={[
            {
              title: 'Mantención',
              dataIndex: 'id_mantencion',
              key: 'id_mantencion',
              width: 80,
              render: (id_mantencion) =>
                id_mantencion ? (
                  <Link
                    to={`${paths.admin.mantenciones.detail(id_mantencion)}?only_edit=1`}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    #{id_mantencion}
                  </Link>
                ) : (
                  <span className="text-xs text-gray-400">N/A</span>
                ),
              defaultSortOrder: 'descend',
              sorter: (a, b) => a.id_mantencion - b.id_mantencion,
              showSorterTooltip: false,
            },
            {
              title: 'Estado',
              dataIndex: 'status',
              width: 80,
              render: (estado) => (
                <Tag color={getColorForStatus(estado)} size="small" className="text-xs font-medium">
                  {estado?.toUpperCase()}
                </Tag>
              ),
              filters: SOLICITUD_TIPOS_ARRAY.map((estado) => ({
                value: estado,
                text: estado,
              })),
              onFilter: (value, record) => record.status === value,
            },
            {
              title: 'Cliente',
              dataIndex: 'Cliente',
              width: 180,
              render: (data) => (
                <div className="min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">{data?.name}</div>
                  <div className="text-xs text-gray-500 truncate">{data?.rut}</div>
                  <div className="text-xs text-gray-400 truncate">
                    {data?.address}, {data?.commune}
                  </div>
                </div>
              ),
            },
            {
              title: 'Encargado',
              dataIndex: 'encargado',
              width: 140,
              render: (value, record, index) => (
                <SpecialistSelect
                  onChange={async (value) => asignSpecialist(record, value, index)}
                  // Migración rut→id: preseleccionar por el id del técnico (companion).
                  defaultValue={record.encargado_user_id}
                  className="w-full"
                />
              ),
            },
            {
              title: 'Hora',
              dataIndex: 'hora_inicio',
              width: 100,
              render: (value, record) => (
                <TimePicker
                  format="HH:mm"
                  minuteStep={15}
                  value={value ? dayjs(value, 'HH:mm:ss') : null}
                  onChange={(time) => setHoraInicio(record, time ? time.format('HH:mm:ss') : null)}
                  placeholder="Todo el día"
                  className="w-full"
                  size="small"
                  allowClear
                />
              ),
            },
            {
              title: 'Acciones',
              dataIndex: 'actions',
              width: 100,
              render: (_, record, index) => {
                const isCompleted = [SOLICITUD_TIPOS.ENVIADA, SOLICITUD_TIPOS.FINALIZADA].includes(
                  record.status
                );

                return (
                  <Button
                    onClick={() => {
                      if (isCompleted) {
                        openDowloadMantencion(record);
                      } else {
                        deleteSolicitudToDay(record, selectedDay, index);
                      }
                    }}
                    type={isCompleted ? 'primary' : 'default'}
                    danger={!isCompleted}
                    size="small"
                    icon={isCompleted ? <DownloadOutlined /> : <MinusOutlined />}
                    className="text-xs"
                  >
                    {isCompleted ? 'DESCARGAR' : 'QUITAR'}
                  </Button>
                );
              },
            },
          ]}
          pagination={{
            pageSize: 8,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} solicitudes`,
            pageSizeOptions: ['5', '8', '15', '25'],
          }}
          locale={{
            emptyText: !selectedDay
              ? 'Seleccione un día para ver las solicitudes'
              : 'No hay solicitudes para este día',
          }}
        />
      </div>
    </div>
  );
};

export default SolicitudesForDayTable;
